import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';
export interface DropdownOption {
  value: string;
  label: string;
}
@Component({
  selector: 'app-single-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],  
  templateUrl: './single-select.html',
  styleUrl: './single-select.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelect),
      multi: true,
    },
  ],
})
export class SingleSelect implements ControlValueAccessor, OnInit    {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() allowSearch: boolean = false;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;
  // ✅ Updated output format to match radio-button component
  @Output() answerChange = new EventEmitter<{ 
    questionId: string; 
    options: { optionId: string; optionSelected: string }[]; 
  }>();

  options: DropdownOption[] = [];
  filteredOptions: DropdownOption[] = [];
  selectedOption: DropdownOption | null = null;
  searchTerm = '';
  dropdownOpen = false;
  disabled = false;
  othersText: string = '';
  private isUpdating: boolean = false;
  private othersOptionId: string | null = null;

  // ControlValueAccessor
  onChange = (value: string) => {};
  onTouched = () => {};

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  ngOnInit() {
    // Cache the Others option ID for faster lookups
    this.othersOptionId = this.findOthersOptionId();
    
    this.options = this.question?.questionOptions?.map(opt => ({
      value: opt.optionId,
      label: opt.text
    })) || [];
    this.filteredOptions = [...this.options];

    if (this.formControl) {
      this.formControl.valueChanges.subscribe(value => {
        if (!this.isUpdating) {
          // Handle array values from parent (for consistency with other components)
          const optionId = Array.isArray(value) ? value[0] : value;
          const previousValue = this.selectedOption?.value || '';
          
          if (optionId) {
            this.selectedOption = this.options.find(opt => opt.value === optionId) || null;
          } else {
            this.selectedOption = null;
          }

          // Only clear others text if switching away from Others option
          if (previousValue === this.othersOptionId && optionId !== this.othersOptionId) {
            this.othersText = '';
          }
        }
      });
    }
  }

  writeValue(value: string): void {
    if (!this.isUpdating) {
      // Handle array values from parent (for consistency with other components)
      const optionId = Array.isArray(value) ? value[0] : value;
      const previousValue = this.selectedOption?.value || '';
      
      if (optionId) {
        this.selectedOption = this.options.find(opt => opt.value === optionId) || null;
      } else {
        this.selectedOption = null;
      }

      // Only clear others text if switching away from Others option
      if (previousValue === this.othersOptionId && optionId !== this.othersOptionId) {
        this.othersText = '';
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown() {
    if (!this.disabled) {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.onTouched();
        if (this.formControl) {
          this.formControl.markAsTouched();
        }
      }
    }
  }

  selectOption(option: DropdownOption) {
    if (this.disabled) return;

    this.isUpdating = true;
    this.selectedOption = option;
    this.dropdownOpen = false;

    // Clear others text if a different option is selected
    if (!this.isOthersSelected()) {
      this.othersText = '';
    }

    this.onChange(option.value);
    this.onTouched();
    this.emitAnswer();

    if (this.formControl) {
      this.formControl.setValue(option.value, { emitEvent: false });
      this.formControl.markAsTouched();
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 0);
  }

  onSearchChange() {
    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter(opt =>
      opt.label.toLowerCase().includes(term)
    );
  }

  get noResults(): boolean {
    return this.allowSearch && this.searchTerm.trim().length > 0 && this.filteredOptions.length === 0;
  }

  isOthersOption(option: DropdownOption): boolean {
    if (!this.question?.questionOptions) return false;
    const originalOption = this.question.questionOptions.find(opt => opt.optionId === option.value);
    return originalOption?.text?.toLowerCase().trim() === 'others';
  }

  isOthersSelected(): boolean {
    if (!this.selectedOption) return false;
    // Use cached othersOptionId for faster and more reliable check
    if (this.othersOptionId) {
      return this.selectedOption.value === this.othersOptionId;
    }
    // Fallback to finding the option
    if (!this.question?.questionOptions) return false;
    const selectedOption = this.question.questionOptions.find(
      (opt) => opt.optionId === this.selectedOption?.value
    );
    return this.isOthersOption({ value: selectedOption?.optionId || '', label: selectedOption?.text || '' });
  }

  private findOthersOptionId(): string | null {
    if (!this.question?.questionOptions) return null;
    const othersOption = this.question.questionOptions.find((opt) => 
      opt.text?.toLowerCase().trim() === 'others'
    );
    return othersOption?.optionId || null;
  }

  onOthersTextChange(text: string) {
    this.othersText = text;
    // Emit answer immediately - writeValue will check if value changed and skip if not
    // This allows continuous typing without interruption
    this.emitAnswer();
  }

  onOthersInputBlur() {
    // Ensure answer is emitted when user leaves the field
    this.emitAnswer();
    this.onTouched();
  }

  private emitAnswer() {
    if (!this.question?.questionId) return;

    const optionId = this.selectedOption?.value || '';
    const optionSelected = this.isOthersSelected() ? this.othersText : '';

    this.answerChange.emit({
      questionId: this.question.questionId,
      options: [{ optionId, optionSelected }],
    });

    console.log('emitAnswer', {
      questionId: this.question.questionId,
      options: [{ optionId, optionSelected }],
    });
  }
}
